
import { defineComponent, reactive, watch } from 'vue';
import { Modal } from 'ant-design-vue';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { useModalBase } from '@/core/composables/ModalBase';
import Icone from '@/core/components/Icone.vue';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import {
  IPedidoCompra, IPedidoCompraDetalhePagamento, IPedidoCompraFrete, IPedidoCompraFreteVolume, IPedidoCompraImposto, IPedidoCompraItem, IPedidoCompraMarcador,
} from '@/models/Entidades/Compras/IPedidoCompra';
import { EStatusPedidoCompra } from '@/models/Enumeradores/Compras/EStatusPedidoCompra';
import ServicoPedidoCompra from '@/servicos/Compras/ServicoPedidoCompras';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { ETipoArquivo } from '@/core/models/Enumeradores/ETipoArquivo';
import { IArquivoPdf } from '@/core/models/IArquivoPdf';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import PreviewPdf from '@/core/components/Modal/PreviewPdf.vue';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import ServicoSistema from '@/servicos/MeuSistema/ServicoSistema';
import storeSistema from '@/store/storeSistema';
import { ISalvamentoAutomatico } from '@/models/Entidades/MeuSistema/ISalvamentoAutomatico';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { useFocusBase } from '@/core/composables/FocusBase';
import SelecionarPessoa from '@/components/Cadastros/Pessoas/SelecionarPessoa.vue';
import { ETipoRelacaoPessoa } from '@/models/Enumeradores/Cadastros/Pessoas/ETipoRelacaoPessoa';
import SelecionarMarcadorNotaFiscal from '@/components/Fiscal/NotasFiscais/SelecionarMarcadorNotaFiscal.vue';
import PedidoCompraItens from '@/components/Compras/PedidoCompra/PedidoCompraItens.vue';
import ServicoCalculosComerciais from '@/servicos/ServicoCalculosComerciais';
import PedidoCompraImpostos from '@/components/Compras/PedidoCompra/PedidoCompraImpostos.vue';
import SelecionarData from '@/core/components/Tela/SelecionarData.vue';
import PedidoCompraDescontoAcrescimos from '@/components/Compras/PedidoCompra/PedidoCompraDescontoAcrescimos.vue';
import PedidoCompraDetalhePagamento from '@/components/Compras/PedidoCompra/PedidoCompraDetalhePagamento.vue';
import PedidoCompraDadosAdicionais from '@/components/Compras/PedidoCompra/PedidoCompraDadosAdicionais.vue';
import UtilitarioDispositivo from '@/core/utilitarios/UtilitarioDispositivo';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import SelecionarTipoOperacao from '@/components/Cadastros/SelecionarTipoOperacao.vue';
import { IDTOPedidoCompraRegistroAtividade } from '@/models/DTO/Compras/IDTOPedidoCompraRegistroAtividade';
import PedidoCompraHistorico from '@/components/Compras/PedidoCompra/PedidoCompraHistorico.vue';
import SelecionarColaborador from '@/components/Cadastros/Pessoas/Colaboradores/SelecionarColaborador.vue';
import SelecionarFormaPagamento from '@/components/Cadastros/Financeiro/SelecionarFormaPagamento.vue';
import ServicoTipoOperacao from '@/servicos/Cadastros/ServicoTipoOperacao';
import PedidoCompraFrete from '@/components/Compras/PedidoCompra/PedidoCompraFrete.vue';
import ServicoFormaPagamento from '@/servicos/Cadastros/Financeiro/ServicoFormaPagamento';

export default defineComponent({
  name: 'PedidoCompraModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    Icone,
    RequisicaoModal,
    PreviewPdf,
    SelecionarTipoOperacao,
    SelecionarPessoa,
    SelecionarMarcadorNotaFiscal,
    PedidoCompraItens,
    PedidoCompraImpostos,
    SelecionarData,
    PedidoCompraDescontoAcrescimos,
    PedidoCompraDetalhePagamento,
    PedidoCompraDadosAdicionais,
    PedidoCompraHistorico,
    SelecionarColaborador,
    SelecionarFormaPagamento,
    PedidoCompraFrete,
  },
  emits: ['update:operacao', 'update:visivel', 'confirmacao', 'concluirEtapa'],
  setup(props, { emit }) {
    const {
      modalBase, apresentarBarraProgresso, ocultarBarraProgresso, apresentarRetornoRequisicao,
    } = useModalBase(props, emit);
    const {
      telaBase, apresentarMensagemAlerta, apresentarMensagemErro, apresentarMensagemSucesso, preencherPermissoesDados, filtrarPermissaoDadosUsuarioMultiEmpresas, obterPermissoes, preencherEmpresasDisponiveis,
    } = useTelaBase();
    const {
      focusBase, proximoElemento, elementoAnterior,
    } = useFocusBase();
    const servicoPedidoCompra = new ServicoPedidoCompra();
    const servicoSistema = new ServicoSistema();
    const servicoCalculosComerciais = new ServicoCalculosComerciais();
    const servicoTipoOperacao = new ServicoTipoOperacao();
    const servicoFormaPagamento = new ServicoFormaPagamento();

    let debounceSalvamentoAutomatico = 0;
    let debounceAtualizacaoTotalizadores = 0;

    const state = reactive({
      pedidoCompra: {} as IPedidoCompra,
      telaMobile: false,
      sincronizando: false,
      sincronizandoItem: false,
      arquivosPdf: [] as IArquivoPdf[],
      totalDetalhesPagamento: 0,
      siglaUf: '',
      marcadores: [] as string[],
      salvamentoAutomatico: {} as ISalvamentoAutomatico,
      recuperarSalvamentoAutomatico: false,
      chaveItens: 0,
      editavel: false,
      valorTotalImpostos: 0,
      novoItem: false,
      registrosAtividade: [] as IDTOPedidoCompraRegistroAtividade[],
      tiposDocumento: [] as number[],
    });

    async function limparTela() {
      state.recuperarSalvamentoAutomatico = false;
      state.salvamentoAutomatico = {} as ISalvamentoAutomatico;
      state.salvamentoAutomatico.codigo = 0;
      state.pedidoCompra = {} as IPedidoCompra;
      state.pedidoCompra.codigo = 0;
      state.pedidoCompra.tipoOperacao = 0;
      state.pedidoCompra.codigoEmpresa = props.operacao.empresaSelecionada;
      state.pedidoCompra.dataCriacao = UtilitarioData.obterDataAtual();
      state.pedidoCompra.status = EStatusPedidoCompra.EmDigitacao;
      state.pedidoCompra.itens = [] as IPedidoCompraItem[];
      state.pedidoCompra.imposto = {} as IPedidoCompraImposto;
      state.pedidoCompra.detalhesPagamentos = [] as IPedidoCompraDetalhePagamento[];
      state.pedidoCompra.frete = {} as IPedidoCompraFrete;
      state.pedidoCompra.frete.codigo = 0;
      state.pedidoCompra.frete.volumes = [] as IPedidoCompraFreteVolume[];
      state.marcadores = [] as string[];
      state.editavel = false;
      state.sincronizando = false;
      state.valorTotalImpostos = 0;
      state.siglaUf = '';
    }

    function obterDescricaoStatus(status: EStatusPedidoCompra) {
      switch (status) {
        case EStatusPedidoCompra.EmAnalise:
          return 'Em ánalise';
        case EStatusPedidoCompra.Confirmado:
          return 'Confirmado';
        case EStatusPedidoCompra.Entregue:
          return 'Entregue';
        case EStatusPedidoCompra.Cancelado:
          return 'Cancelado';
        default:
          return 'Em Digitação';
      }
    }

    function obterCorStatus(status: EStatusPedidoCompra) {
      switch (status) {
        case EStatusPedidoCompra.EmAnalise:
          return '#daa520';
        case EStatusPedidoCompra.Confirmado:
          return '#2e8b57';
        case EStatusPedidoCompra.Entregue:
          return '#1843b8';
        case EStatusPedidoCompra.Cancelado:
          return '#000000';
        default:
          return '#ff7f50';
      }
    }

    function obtemIdentificadorSalvamentoAutomatico(): string {
      return `${telaBase.identificadorRecurso}_${state.pedidoCompra.codigo}`;
    }

    function preencherMarcadores() {
      const marcadores = [] as IPedidoCompraMarcador[];

      state.marcadores.forEach((m) => {
        const marcador = {} as IPedidoCompraMarcador;
        marcador.pedidoCompra = state.pedidoCompra.codigo;
        marcador.marcador = m;
        marcador.ordem = marcadores.length + 1;
        marcadores.push(marcador);
      });
      return marcadores;
    }

    async function obterMarcadores() {
      state.pedidoCompra.marcadores.forEach((m) => {
        state.marcadores.push(m.marcador);
      });
    }

    function atualizarTotalDetalhesPagamento() {
      state.totalDetalhesPagamento = 0;
      state.pedidoCompra.detalhesPagamentos.forEach((d) => {
        state.totalDetalhesPagamento += d.valor;
      });
    }

    async function salvamentoAutomatico() {
      if (state.pedidoCompra.codigo > 0) {
        clearTimeout(debounceSalvamentoAutomatico);
        debounceSalvamentoAutomatico = setTimeout(async () => {
          const identificador = obtemIdentificadorSalvamentoAutomatico();
          state.salvamentoAutomatico.data = UtilitarioData.obterDataAtual();
          state.salvamentoAutomatico.codigoEmpresa = state.pedidoCompra.codigoEmpresa;
          state.salvamentoAutomatico.codigoUsuario = storeSistema.getters.codigoUsuarioAutenticado();
          state.salvamentoAutomatico.identificador = identificador;
          state.salvamentoAutomatico.dados = JSON.stringify(state.pedidoCompra);
          // Salva informações - LocalStorage
          await servicoSistema.salvamentoAutomaticoLocalStorage(`${identificador}_${state.salvamentoAutomatico.codigoUsuario}`, state.salvamentoAutomatico);
          // Salva informações - API Servidor
          const retornoSalvamento = await servicoSistema.salvamentoAutomaticoApi(state.salvamentoAutomatico);
          if (retornoSalvamento.status === EStatusRetornoRequisicao.Sucesso) {
            state.salvamentoAutomatico.codigo = retornoSalvamento.codigoRegistro;
          }
          state.recuperarSalvamentoAutomatico = false;
        }, 1000);
      }
    }

    async function atualizarFormaPagamento() {
      if (state.pedidoCompra.codigoFormaPagamento === 0 || state.pedidoCompra.codigoFormaPagamento === undefined) {
        const tipoOperacao = await servicoTipoOperacao.obter(state.pedidoCompra.tipoOperacao, state.pedidoCompra.codigoEmpresa);
        if (UtilitarioGeral.validaCodigo(tipoOperacao.codigoFormaPagamento)) {
          state.pedidoCompra.codigoFormaPagamento = tipoOperacao.codigoFormaPagamento;
          const tipoDocumentos = await servicoFormaPagamento.obterTiposDocumentos(state.pedidoCompra.codigoFormaPagamento, state.pedidoCompra.codigoEmpresa);
          tipoDocumentos.forEach((td) => {
            state.tiposDocumento.push(td.codigo);
          });
        }
      }
    }
    // if (state.pedidoCompra.tipoOperacao === 0 || state.pedidoCompra.tipoOperacao === undefined) {
    //    state.tipo = {} as IFormaPagamento;
    // } else if (state.recebimento.codigoFormaPagamento !== 0 && state.recebimento.codigoFormaPagamento !== undefined && state.recebimento.codigoFormaPagamento !== state.formaPagamento.codigo) {
    //    state.formaPagamento = await servicoFormaPagamento.obter(state.recebimento.codigoFormaPagamento);
    //   state.formaPagamento.tiposDocumentosFinanceiro = await servicoFormaPagamento.obterTiposDocumentos(state.recebimento.codigoFormaPagamento, state.recebimento.codigoEmpresa);
    //    state.formaPagamento.tabelasPreco = await servicoFormaPagamento.obterTabelasPreco(state.recebimento.codigoFormaPagamento, state.recebimento.codigoEmpresa);
    //  }
    //  salvamentoAutomatico();
    // }

    async function obterPedidoCompra(codigo: number) {
      telaBase.carregando = true;
      const pedidoCompra = await servicoPedidoCompra.obter(codigo, props.operacao.empresaSelecionada);
      pedidoCompra.itens = await servicoPedidoCompra.obterItensPedidosCompletos(codigo, props.operacao.empresaSelecionada);
      pedidoCompra.detalhesPagamentos = await servicoPedidoCompra.obterDetalhePagamento(codigo);
      telaBase.carregando = false;
      state.pedidoCompra = pedidoCompra;
      obterMarcadores();

      const identificador = obtemIdentificadorSalvamentoAutomatico();
      const salvamentoAutomaticoApi = await servicoSistema.obterUltimoSalvamentoAutomaticoApi(state.pedidoCompra.codigoEmpresa, storeSistema.getters.codigoUsuarioAutenticado(), identificador);
      const salvamentoAutomaticoLocalStorage = await servicoSistema.obterSalvamentoAutomaticoLocalStorage(`${identificador}_${storeSistema.getters.codigoUsuarioAutenticado()}`);
      state.salvamentoAutomatico = servicoSistema.obterSalvamentoMaisRecente(salvamentoAutomaticoApi, salvamentoAutomaticoLocalStorage);

      state.chaveItens += 1;
      atualizarTotalDetalhesPagamento();
      // atualizarTipoOperacao();
      state.editavel = state.pedidoCompra.status === EStatusPedidoCompra.EmDigitacao;
    }

    async function salvar(salvarNovo: boolean) {
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };

      apresentarBarraProgresso('Aguarde por favor... Estamos salvando o pedido de compra');
      state.pedidoCompra.marcadores = preencherMarcadores();
      if (state.pedidoCompra.codigo > 0) {
        retorno = await servicoPedidoCompra.alterar(state.pedidoCompra);
      } else {
        retorno = await servicoPedidoCompra.incluir(state.pedidoCompra);
      }
      ocultarBarraProgresso();
      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        emit('confirmacao');
        apresentarMensagemSucesso(retorno.mensagem);
        const identificador = obtemIdentificadorSalvamentoAutomatico();
        servicoSistema.removerSalvamentoAutomaticoLocalStorage(`${identificador}_${storeSistema.getters.codigoUsuarioAutenticado()}`);
        await servicoSistema.removerSalvamentoAutomaticoApi(state.pedidoCompra.codigoEmpresa, storeSistema.getters.codigoUsuarioAutenticado(), identificador);

        if (state.pedidoCompra.codigo === 0) {
          await obterPedidoCompra(retorno.codigoRegistro);
        } else if (salvarNovo) {
          limparTela();
          const telaOperacao: ITelaOperacao = props.operacao;
          telaOperacao.codigoRegistro = 0;
          telaOperacao.tipoPermissaoDados = EPermissaoDados.Incluir;
          modalBase.computedOperacao = telaOperacao;
        } else {
          modalBase.computedVisivel = false;
        }
      } else {
        apresentarRetornoRequisicao(retorno);
      }
    }

    function validarDetalhesPagamento(salvarNovo: boolean) {
    //  Modal.confirm({
    //    title: 'O total dos detalhes de pagamento está diferente do total do recebimento!',
    //    content: 'Realmente deseja salvar o recebimento assim?',
    //    okText: 'Sim',
    //    okType: 'danger',
    //    cancelText: 'Não',
    //    autoFocusButton: null,
    //    onOk: () => {
      salvar(salvarNovo);
    //    },
    //  });
    }

    function validarCampos(salvarNovo: boolean) {
      if (!UtilitarioGeral.validaCodigo(state.pedidoCompra.codigoPessoaFornecedor)) {
        apresentarMensagemAlerta('O fornecedor deve ser informado!');
        return;
      }

      if (!UtilitarioGeral.validaCodigo(state.pedidoCompra.tipoOperacao)) {
        apresentarMensagemAlerta('O tipo de operação deve ser informado!');
        return;
      }

      if (!UtilitarioGeral.validaCodigo(state.pedidoCompra.codigoFormaPagamento)) {
        apresentarMensagemAlerta('A forma de pagamento deve ser informada!');
        return;
      }

      const indexLinhaBranco = state.pedidoCompra.itens.findIndex((c) => c.codigoProdutoDefinicao === undefined || c.codigoProdutoDefinicao === 0);
      if (indexLinhaBranco >= 0) {
        state.pedidoCompra.itens.splice(indexLinhaBranco, 1);
      }
      if (state.pedidoCompra.totalPedido > 0 && state.totalDetalhesPagamento !== state.pedidoCompra.totalPedido) {
        validarDetalhesPagamento(salvarNovo);
      } else {
        salvar(salvarNovo);
      }
    }

    function recuperarSalvamentoAutomatico() {
      if (UtilitarioGeral.valorValido(state.salvamentoAutomatico.dados)) {
        const pedidoCompraMercadoriaSalvoAutomatico = JSON.parse(state.salvamentoAutomatico.dados);
        if (UtilitarioGeral.objetoValido(pedidoCompraMercadoriaSalvoAutomatico)) {
          state.pedidoCompra = pedidoCompraMercadoriaSalvoAutomatico;
          state.recuperarSalvamentoAutomatico = false;
          apresentarMensagemSucesso('Informações recuperadas com sucesso!');
        }
      }
    }

    function concluirEtapa() {
      Modal.confirm({
        title: `Realmente deseja passar da etapa ${obterDescricaoStatus(state.pedidoCompra.status)} para a etapa ${obterDescricaoStatus(state.pedidoCompra.status + 1)}?`,
        okText: 'Sim',
        okType: 'danger',
        cancelText: 'Não',
        autoFocusButton: null,
        onOk: async () => {
          const retorno = await servicoPedidoCompra.alterarEtapa(state.pedidoCompra.codigo, state.pedidoCompra.codigoEmpresa, state.pedidoCompra.status + 1);
          if (retorno.status === 1) {
            state.pedidoCompra.status += 1;
            emit('confirmacao');
          } else if (retorno.status === 2) {
            apresentarMensagemAlerta(retorno.mensagem);
          } else {
            apresentarMensagemErro(retorno.mensagem);
          }
        },
      });
    }

    function retornarEtapa() {
      Modal.confirm({
        title: `Realmente deseja passar da etapa ${obterDescricaoStatus(state.pedidoCompra.status)} para a etapa ${obterDescricaoStatus(state.pedidoCompra.status - 1)}?`,
        okText: 'Sim',
        okType: 'danger',
        cancelText: 'Não',
        autoFocusButton: null,
        onOk: async () => {
          const retorno = await servicoPedidoCompra.alterarEtapa(state.pedidoCompra.codigo, state.pedidoCompra.codigoEmpresa, state.pedidoCompra.status - 1);
          if (retorno.status === 1) {
            state.pedidoCompra.status -= 1;
            emit('confirmacao');
          } else if (retorno.status === 2) {
            apresentarMensagemAlerta(retorno.mensagem);
          } else {
            apresentarMensagemErro(retorno.mensagem);
          }
        },
      });
    }

    function cancelar() {
      Modal.confirm({
        title: 'Realmente deseja cancelar o pedido de compra?',
        okText: 'Sim',
        okType: 'danger',
        cancelText: 'Não',
        autoFocusButton: null,
        onOk: async () => {
          const retorno = await servicoPedidoCompra.alterarEtapa(state.pedidoCompra.codigo, state.pedidoCompra.codigoEmpresa, EStatusPedidoCompra.Cancelado);
          if (retorno.status === 1) {
            state.pedidoCompra.status = EStatusPedidoCompra.Cancelado;
            emit('confirmacao');
          } else if (retorno.status === 2) {
            apresentarMensagemAlerta(retorno.mensagem);
          } else {
            apresentarMensagemErro(retorno.mensagem);
          }
        },
      });
    }

    function excluir() {
      Modal.confirm({
        title: 'Realmente deseja excluir o pedido de compra?',
        okText: 'Sim',
        okType: 'danger',
        cancelText: 'Não',
        autoFocusButton: null,
        onOk: async () => {
          const retorno = await servicoPedidoCompra.excluirVarios(state.pedidoCompra.codigoEmpresa, [state.pedidoCompra.codigo]);
          if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
            apresentarMensagemSucesso('pedido de compra excluído com sucesso!');
            emit('confirmacao');
            modalBase.computedVisivel = false;
          } else if (UtilitarioGeral.valorValido(retorno.mensagem)) {
            apresentarMensagemErro(retorno.mensagem);
          } else {
            apresentarMensagemErro('Não foi possível excluir o pedido de compra.');
          }
        },
      });
    }

    async function atualizarValoresTotais() {
      clearTimeout(debounceAtualizacaoTotalizadores);
      debounceAtualizacaoTotalizadores = setTimeout(async () => {
        state.pedidoCompra.totalProdutos = 0;
        state.pedidoCompra.totalDesconto = 0;
        // state.pedidoCompra.totalFrete = 0;
        state.pedidoCompra.totalOutrasDespesas = 0;
        state.pedidoCompra.totalPedido = 0;
        state.valorTotalImpostos = 0;

        for (let index = 0; index < state.pedidoCompra.itens.length; index += 1) {
          if (UtilitarioGeral.validaCodigo(state.pedidoCompra.itens[index].codigoProdutoDefinicao)) {
            state.pedidoCompra.itens[index].valorTotal = servicoCalculosComerciais.calculaValorTotal(state.pedidoCompra.itens[index].quantidade, state.pedidoCompra.itens[index].valorUnitario, state.pedidoCompra.itens[index].desconto, 0);
            state.pedidoCompra.totalProdutos += state.pedidoCompra.itens[index].quantidade * state.pedidoCompra.itens[index].valorUnitario;
            state.pedidoCompra.totalDesconto += state.pedidoCompra.itens[index].desconto;
          }
        }

        state.pedidoCompra.totalPedido = state.pedidoCompra.totalProdutos;
        state.pedidoCompra.imposto.valorIpi = 0;
        state.pedidoCompra.imposto.valorIcms = 0;
        state.pedidoCompra.imposto.baseCalculoICMS = 0;
        state.pedidoCompra.imposto.baseCalculoIpi = 0;

        for (let index = 0; index < state.pedidoCompra.itens.length; index += 1) {
          if (UtilitarioGeral.validaCodigo(state.pedidoCompra.itens[index].codigoProdutoDefinicao)) {
            const subTotalProduto = servicoCalculosComerciais.formatarValor2CasasDecimais((state.pedidoCompra.itens[index].quantidade * state.pedidoCompra.itens[index].valorUnitario));
            if (parseFloat((subTotalProduto).toFixed(2)) < parseFloat(((state.pedidoCompra.itens[index].desconto)).toFixed(2))) {
              state.pedidoCompra.itens[index].desconto = 0;
            }

            const retornoCalculoTributacoes = await servicoCalculosComerciais.CalcularTributacoesPedidoCompra(state.pedidoCompra.itens[index]);

            state.pedidoCompra.itens[index].imposto.valorIcms = retornoCalculoTributacoes.icms.valorIcms;
            state.pedidoCompra.imposto.valorIcms += state.pedidoCompra.itens[index].imposto.valorIcms;
            state.pedidoCompra.imposto.baseCalculoICMS += state.pedidoCompra.itens[index].valorTotal;

            state.pedidoCompra.itens[index].imposto.valorIpi = retornoCalculoTributacoes.ipi.valor;
            state.pedidoCompra.imposto.valorIpi += state.pedidoCompra.itens[index].imposto.valorIpi;
            state.pedidoCompra.imposto.baseCalculoIpi += state.pedidoCompra.itens[index].valorTotal;

            state.pedidoCompra.itens[index].valorTotal += state.pedidoCompra.itens[index].imposto.valorIpi;
          }
        }
        state.valorTotalImpostos = state.pedidoCompra.imposto.valorIcms + state.pedidoCompra.imposto.valorIpi;
        if (state.pedidoCompra.imposto.valorIpi === undefined) {
          state.pedidoCompra.imposto.valorIpi = 0;
        }
        if (state.pedidoCompra.totalFrete === undefined) {
          state.pedidoCompra.totalFrete = 0;
        }
        if (state.pedidoCompra.totalOutrasDespesas === undefined) {
          state.pedidoCompra.totalOutrasDespesas = 0;
        }
        if (state.pedidoCompra.totalOutrasDespesas === undefined) {
          state.pedidoCompra.totalOutrasDespesas = 0;
        }
        if (state.pedidoCompra.totalDesconto === undefined) {
          state.pedidoCompra.totalDesconto = 0;
        }
        state.pedidoCompra.totalPedido = state.pedidoCompra.totalProdutos + state.pedidoCompra.imposto.valorIpi + state.pedidoCompra.totalFrete + state.pedidoCompra.totalOutrasDespesas - state.pedidoCompra.totalDesconto;
        salvamentoAutomatico();
      }, 600);
    }

    async function atualizarItem(index: number) {
      await atualizarValoresTotais();

      if (state.pedidoCompra.itens[index].codigo === undefined || state.pedidoCompra.itens[index].codigo === 0) {
        state.pedidoCompra.itens[index].pedidoCompra = state.pedidoCompra.codigo;
      }
    }

    async function excluirItem() {
      atualizarValoresTotais();
    }

    async function atualizarDescontoGeral() {
      for (let index = 0; index < state.pedidoCompra.itens.length; index += 1) {
        if (UtilitarioGeral.validaCodigo(state.pedidoCompra.itens[index].codigoProdutoDefinicao)) {
          const subTotalProduto = servicoCalculosComerciais.formatarValor2CasasDecimais((state.pedidoCompra.itens[index].quantidade * state.pedidoCompra.itens[index].valorUnitario));

          if (state.pedidoCompra.totalDesconto > 0) {
            state.pedidoCompra.itens[index].desconto = servicoCalculosComerciais.calculaValorRateio(subTotalProduto, state.pedidoCompra.totalProdutos, state.pedidoCompra.totalDesconto);
          } else {
            state.pedidoCompra.itens[index].desconto = 0;
          }
        }
      }
      atualizarValoresTotais();
    }

    async function atualizarDetalhePagamento(index: number) {
      if (state.pedidoCompra.codigo === undefined || state.pedidoCompra.codigo === 0) return;
      atualizarTotalDetalhesPagamento();

      if (state.pedidoCompra.detalhesPagamentos[index].codigo === undefined || state.pedidoCompra.detalhesPagamentos[index].codigo === 0) {
        state.pedidoCompra.detalhesPagamentos[index].pedidoCompra = state.pedidoCompra.codigo;
      }
    }

    async function imprimir() {
      const retornoRelatorio = await servicoPedidoCompra.ImprimirPedidoCompra(state.pedidoCompra.codigo, state.pedidoCompra.codigoEmpresa, ETipoArquivo.PDF);
      telaBase.carregando = false;
      if (retornoRelatorio.status === EStatusRetornoRequisicao.Sucesso) {
        state.arquivosPdf = [{ nome: '', link: retornoRelatorio.link } as IArquivoPdf];
      } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Alerta) {
        apresentarMensagemAlerta(retornoRelatorio.mensagem);
      } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Erro) {
        apresentarMensagemErro(retornoRelatorio.mensagem);
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      state.telaMobile = UtilitarioDispositivo.larguraTelaMobile();
      limparTela();
      state.editavel = true;
      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) {
          await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        } else {
          await obterPermissoes(ETipoPermissao.Dados);
        }
        await preencherEmpresasDisponiveis();
        telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
        if (UtilitarioGeral.validaCodigo(props.operacao.codigoRegistro)) {
          await obterPedidoCompra(props.operacao.codigoRegistro);
        }
      }
    });

    return {
      props,
      modalBase,
      state,
      EPermissaoDados,
      telaBase,
      EStatusPedidoCompra,
      focusBase,
      concluirEtapa,
      retornarEtapa,
      cancelar,
      excluir,
      imprimir,
      validarCampos,
      obterCorStatus,
      obterDescricaoStatus,
      UtilitarioMascara,
      recuperarSalvamentoAutomatico,
      salvamentoAutomatico,
      proximoElemento,
      elementoAnterior,
      ETipoRelacaoPessoa,
      atualizarItem,
      excluirItem,
      atualizarValoresTotais,
      atualizarDescontoGeral,
      atualizarDetalhePagamento,
      atualizarFormaPagamento,
    };
  },
});
